import { useEffect, useMemo, useState } from "react";
import { useSearchspringSearchContext } from "@app/providers/searchspring";
import { SearchspringSearchResult } from "@app/types";
import { useSearchspringOption } from "./use-searchspring-option";

type SearchspringItems = Array<SearchspringSearchResult | string>;

export function useSearchspringResults() {
  const [loadedPages, setLoadedPages] = useState(new Set<number>());

  const { response } = useSearchspringSearchContext();

  const page = useSearchspringOption("page", {
    defaultValue: 1,
  });

  // Updating inline banners
  const inlineBanners = useMemo(() => {
    if (!response || !response.merchandising.content.inline) return new Map<number, string>();
    return response.merchandising.content.inline.reduce((map, item) => {
      const isBefore = item.config.position.index < response.pagination.begin;
      const isAfter = item.config.position.index > response.pagination.end;
      if (isBefore || isAfter) return map;

      map.set(item.config.position.index, item.value);
      return map;
    }, new Map<number, string>());
  }, [response]);

  const currentResults = useMemo(() => {
    if (!response) return [];
    return response.results.reduce((results, result, index) => {
      if (!result) return results;
      const card = inlineBanners.get(index);
      if (card) return [...results, card, result];
      else return [...results, result];
    }, [] as SearchspringItems);
  }, [response, inlineBanners]);

  const [resultPages, setResultPages] = useState(new Map<number, SearchspringItems>());

  useEffect(() => {
    if (!response) return;
    setResultPages((prev) => {
      const next = new Map(prev);
      const page = response.pagination.currentPage;
      next.set(page, currentResults);
      return next;
    });
  }, [currentResults]);

  const results = useMemo(() => {
    return Array.from(resultPages.values()).reduce((results, pageResults) => {
      return [...results, ...pageResults];
    }, [] as SearchspringItems);
  }, [resultPages]);

  useEffect(() => {}, [page, currentResults]);

  return results;
}
